import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
// SPDX-License-Identifier: AGPL-3.0-only
import { Database, HardDrive, Unplug, Wifi } from 'lucide-react';
import { cn } from '../../lib/cn.js';
import { badgeVariants } from '../badge/Badge.js';
/**
 * The tone column, mapped onto the shared `Tone` vocabulary: "muted" is
 * `neutral` (surface-3 + fg-muted), "accent-soft" is `accent` (accent-soft +
 * accent), "warn" is `warn`. No new tints — the whole point of the vocabulary
 * is that a chip cannot invent a colour.
 *
 * ICONS: `hard-drive` for `local` is specified. The rest follow
 * `System States.dc.html`, whose "Database unreachable" state is a
 * `database` glyph, and whose disconnected idiom is a slashed/broken plug —
 * hence `Unplug` for the warn state, so the two remote states differ by more
 * than colour at 11px. (They also differ by label, which is what keeps this
 * clear of WCAG 1.4.1; the icon is belt and braces.)
 */
const STATE_STYLES = Object.freeze({
    local: { icon: HardDrive, tone: 'neutral' },
    'lan-share': { icon: Wifi, tone: 'accent' },
    'remote-db': { icon: Database, tone: 'neutral' },
    'remote-db-offline': { icon: Unplug, tone: 'warn' },
});
/**
 * The desktop runtime chip: what this Adminium is, and whether its data is
 * where you think it is. Sits in the topbar next to the environment area,
 * desktop only.
 *
 * Fed by `GET /api/v1/system/info` + a connection-health poll — never by the
 * preload bridge (is explicit, explains why: the server is the authority for
 * feature gating, the bridge only for native affordances).
 */
export function RuntimeChip({ state, label, onClick, description, className }) {
    const { icon: Icon, tone } = STATE_STYLES[state];
    const content = (_jsxs(_Fragment, { children: [_jsx(Icon, { "aria-hidden": "true", className: "size-3 shrink-0" }), label] }));
    const shared = {
        'data-part': 'runtime-chip',
        'data-state': state,
        ...(description === undefined ? {} : { title: description }),
    };
    if (onClick === undefined) {
        return (_jsx("span", { ...shared, className: cn(badgeVariants({ tone }), className), children: content }));
    }
    return (_jsx("button", { type: "button", ...shared, onClick: onClick, className: cn(badgeVariants({ tone }), 'cursor-pointer transition-[filter] hover:brightness-95 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent', className), children: content }));
}
//# sourceMappingURL=RuntimeChip.js.map